
import { Check, Server, Zap, Shield, Clock, Settings, Headphones, RotateCcw, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import NetworkSection from '@/components/NetworkSection';
import SEOMetadata from '@/components/SEOMetadata';
import { secureOpenUrl } from '@/utils/linkValidation';

const BaremetalServers = () => {
  const scrollToServerTiers = () => {
    const serverTiersSection = document.getElementById('server-tiers');
    if (serverTiersSection) {
      serverTiersSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const serverTiers = [
    {
      name: "Entry",
      cpu: "Intel Xeon E5-2670 v3",
      ram: "32GB DDR4",
      storage: "120GB SSD",
      bandwidth: "10TB/month",
      location: "Ashburn, VA",
      deployment: "2-4 hours",
      price: "$70/month",
      features: ["Full Root Access", "Remote KVM"],
      link: "https://acc.nexx.net/cart.php?a=add&pid=11"
    },
    {
      name: "Performance",
      cpu: "Intel Xeon E5-2699 v3",
      ram: "32GB DDR4",
      storage: "120GB SSD",
      bandwidth: "20TB/month",
      location: "Ashburn, VA",
      deployment: "2-4 hours",
      price: "$180/month",
      features: ["Full Root Access", "Remote KVM", "Priority Support"],
      popular: true,
      link: "https://acc.nexx.net/cart.php?a=add&pid=12"
    },
    {
      name: "Custom",
      cpu: "Your Specifications",
      ram: "Up to 512GB+",
      storage: "Custom Configuration",
      bandwidth: "Unmetered Options",
      location: "Maryland / Ashburn",
      deployment: "24-48 hours",
      price: "Quote",
      features: ["Fully Customizable", "Dedicated Support", "SLA Guarantees"]
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: "Instant Deployment",
      description: "Servers ready in hours, not days"
    },
    {
      icon: Settings,
      title: "Full Root Access",
      description: "Complete control over your environment"
    },
    {
      icon: Headphones,
      title: "Skilled Technical Support",
      description: "The best sys-admins in the business"
    }
  ];

  const faqs = [
    {
      question: "Can I upgrade the server after deployment?",
      answer: "Yes, we offer flexible upgrade options for RAM, storage, and bandwidth. Contact our support team to discuss your upgrade requirements and we'll schedule the changes with minimal downtime."
    },
    {
      question: "Do you offer IPMI/KVM access?",
      answer: "All our servers include remote KVM access for out-of-band management. This allows you to access your server even if the operating system is unresponsive, manage BIOS settings, and perform remote installations."
    },
    {
      question: "How do you handle bandwidth billing?",
      answer: "We offer both metered and unmetered bandwidth options. Metered plans include generous monthly allowances with reasonable overage rates. Unmetered plans provide unlimited bandwidth at consistent speeds."
    },
    {
      question: "Can I colocate my own server instead?",
      answer: "For all colocation solutions - we have our trusted partner, NCOLO that specializes in that area.",
      hasPartner: true,
      partnerName: "Powered by NCOLO"
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Bare Metal Servers",
    "provider": {
      "@type": "Organization",
      "name": "NEXX TELECOM"
    },
    "description": "Enterprise-grade bare metal servers with instant deployment, full root access, and 24/7 support. Custom configurations available.",
    "serviceType": "Bare Metal Server Hosting",
    "areaServed": "Global",
    "offers": [
      {
        "@type": "Offer",
        "name": "Entry Bare Metal Server",
        "price": "70",
        "priceCurrency": "USD",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": "70",
          "priceCurrency": "USD",
          "billingDuration": "P1M"
        }
      },
      {
        "@type": "Offer", 
        "name": "Performance Bare Metal Server",
        "price": "180",
        "priceCurrency": "USD",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": "180",
          "priceCurrency": "USD",
          "billingDuration": "P1M"
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOMetadata
        title="Bare Metal Servers | Dedicated Server Hosting Ashburn | NEXX"
        description="Enterprise-grade bare metal servers with instant deployment in Ashburn, VA. Full root access, custom configurations, and 24/7 support. Starting at $70/month."
        keywords="bare metal servers, dedicated servers, Ashburn servers, enterprise hosting, custom server builds, full root access, server rental"
        canonical="https://nexx.net/baremetal-servers"
        structuredData={structuredData}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-8 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        {/* Floating Server Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
          {/* Floating Server Icons */}
          <div className="absolute top-16 left-12 opacity-5 animate-float-up" style={{ animationDuration: '8s' }}>
            <Server className="w-8 h-8 text-nexx-green" />
          </div>
          <div className="absolute top-32 right-16 opacity-5 animate-float-up" style={{ animationDuration: '10s', animationDelay: '2s' }}>
            <Server className="w-12 h-12 text-blue-500" />
          </div>
          <div className="absolute top-48 left-1/4 opacity-8 animate-float-up" style={{ animationDuration: '12s', animationDelay: '4s' }}>
            <Server className="w-6 h-6 text-gray-400" />
          </div>
          <div className="absolute top-60 right-1/3 opacity-5 animate-float-up" style={{ animationDuration: '9s', animationDelay: '1s' }}>
            <Server className="w-10 h-10 text-nexx-green" />
          </div>
          <div className="absolute bottom-40 left-16 opacity-5 animate-float-up" style={{ animationDuration: '11s', animationDelay: '3s' }}>
            <Server className="w-7 h-7 text-green-400" />
          </div>
          <div className="absolute bottom-24 right-20 opacity-5 animate-float-up" style={{ animationDuration: '13s', animationDelay: '5s' }}>
            <Server className="w-9 h-9 text-blue-400" />
          </div>
          <div className="absolute top-24 left-2/3 opacity-8 animate-float-up" style={{ animationDuration: '7s', animationDelay: '2.5s' }}>
            <Server className="w-5 h-5 text-gray-500" />
          </div>
          <div className="absolute bottom-60 left-1/3 opacity-5 animate-float-up" style={{ animationDuration: '14s', animationDelay: '1.5s' }}>
            <Server className="w-11 h-11 text-nexx-green" />
          </div>

          {/* Tech Background Elements */}
          <div className="absolute top-20 left-10 w-32 h-32 bg-nexx-green/5 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-blue-500/5 rounded-lg rotate-45 animate-bounce" style={{ animationDuration: '3s' }}></div>
          <div className="absolute bottom-40 left-20 w-20 h-20 bg-green-500/5 rounded-full animate-ping" style={{ animationDelay: '1s', animationDuration: '4s' }}></div>
          <div className="absolute bottom-20 right-40 w-16 h-16 bg-nexx-green/5 rounded-lg animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 mt-8">
              Reliable <span className="text-nexx-green">Bare Metal Servers</span>
            </h1>
            <div className="text-2xl lg:text-3xl text-gray-600 mb-6 overflow-hidden relative">
              <span className="inline-block animate-grow-scale bg-gradient-to-r from-nexx-green to-blue-500 bg-clip-text text-transparent font-semibold">
                We're not here to win the server arms race.
              </span>
            </div>
            <p className="text-lg text-gray-500 mb-4 max-w-3xl mx-auto">
              We don't stock endless racks of the latest hardware hoping to resell at scale. We focus on <span className="relative inline-block after:content-[''] after:absolute after:w-full after:h-0.5 after:bottom-0 after:left-0 after:bg-nexx-green after:animate-[grow-width_1.5s_ease-out_1s_forwards] font-semibold text-gray-700">engineering solutions that work</span>—for real-world needs, not marketing slides.
            </p>
            <p className="text-lg text-gray-500 mb-10 max-w-3xl mx-auto">
              Our infrastructure is <span className="relative inline-block after:content-[''] after:absolute after:w-full after:h-0.5 after:bottom-0 after:left-0 after:bg-nexx-green after:animate-[grow-width_1.5s_ease-out_1.8s_forwards] font-semibold text-gray-700">purpose-built, not prepackaged</span>. If you're looking for something tailored—not just off-the-shelf—we'd <span className="relative inline-flex items-center text-red-500 font-semibold animate-pulse">love <Heart className="w-4 h-4 ml-1 animate-bounce fill-red-500" /></span> to build it with you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-nexx-green hover:bg-green-700 text-white px-8 py-4 text-lg"
                onClick={scrollToServerTiers}
              >
                Order Now
              </Button>
              <Button size="lg" variant="outline" className="border-nexx-green text-nexx-green hover:bg-nexx-green hover:text-white px-8 py-4 text-lg">
                Book Consultation
              </Button>
              <Link to="/baremetal-tos">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white px-8 py-4 text-lg"
                >
                  Read Our TOS
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Server Tiers Grid with Benefits */}
      <section id="server-tiers" className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {serverTiers.map((tier, index) => (
              <Card key={tier.name} className={`relative ${tier.popular ? 'border-nexx-green shadow-lg scale-105' : 'border-gray-200'}`}>
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-nexx-green text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl font-bold text-center">{tier.name}</CardTitle>
                  <CardDescription className="text-center">
                    <span className="text-2xl font-bold text-nexx-green">{tier.price}</span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div>
                      <span className="font-medium text-gray-900">CPU:</span>
                      <p className="text-sm text-gray-600">{tier.cpu}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-900">RAM:</span>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-gray-600">{tier.ram}</p>
                        {tier.name !== 'Custom' && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                            Upgradable
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-900">Storage:</span>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-gray-600">{tier.storage}</p>
                        {tier.name !== 'Custom' && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                            Upgradable
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-900">Bandwidth:</span>
                      <p className="text-sm text-gray-600">{tier.bandwidth}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-900">Location:</span>
                      <p className="text-sm text-gray-600">{tier.location}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-900">Deployment:</span>
                      <p className="text-sm text-gray-600">{tier.deployment}</p>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4">
                    <ul className="space-y-2">
                      {tier.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm">
                          <Check className="h-4 w-4 text-nexx-green mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <Button 
                    className={`w-full mt-6 ${tier.popular ? 'bg-nexx-green hover:bg-green-700' : 'bg-gray-900 hover:bg-gray-800'} text-white`}
                    onClick={() => {
                      if (tier.link) {
                        secureOpenUrl(tier.link);
                      }
                    }}
                  >
                    {tier.name === 'Custom' ? 'Get Quote' : 'Order Now'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mb-8">
            <p className="text-gray-600 text-lg">
              Need something specific? We build to spec. 
              <Button variant="link" className="text-nexx-green p-0 ml-2 font-semibold">
                Contact us for custom server builds
              </Button>
            </p>
          </div>

          {/* Benefits integrated into the same section */}
          <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto mt-16">
            {benefits.map((benefit, index) => (
              <div key={benefit.title} className="text-center group">
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-nexx-green group-hover:scale-110 transition-all duration-300">
                  <benefit.icon className="h-6 w-6 text-nexx-green group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">{benefit.title}</h3>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Network Section - Greatest minds with $0 pricing */}
      <NetworkSection />

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Everything you need to know about our servers</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-white border border-gray-200 rounded-lg px-6">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-nexx-green">
                  <div className="flex items-center gap-3">
                    {faq.question}
                    {faq.hasPartner && (
                      <Badge className="bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100">
                        {faq.partnerName}
                      </Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-4">
                  <div className="space-y-4">
                    <p>{faq.answer}</p>
                    {faq.hasPartner && (
                      <Button 
                        onClick={() => window.open('https://ncolo.net', '_blank')}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        Visit NCOLO
                      </Button>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BaremetalServers;
